import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MessageCircle, MessagesSquare, UserRound, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, usePeople } from '../../context';
import { apiGetPingConversations, apiStartPrivatePing } from '../../api';
import './PeopleUI.css';

const initialsOf = (name = '') => String(name || '?').trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase() || '?';
const roleLabel = (role) => ({ admin: 'Administrator', project_manager: 'Project manager', production: 'Production team', client: 'Client' })[role] || 'Portal user';

export function PersonAvatar({ person, name, src, size = 'md', unread = false, className = '' }) {
  const displayName = person?.name || name || 'Portal user';
  const image = person?.avatarData || src || '';
  return (
    <span className={`person-avatar person-avatar-${size} ${className}`.trim()} title={displayName} aria-hidden="true">
      {image ? <img src={image} alt="" /> : initialsOf(displayName)}
      {unread && <i className="person-avatar-unread" />}
    </span>
  );
}

export function UserIdentity({ person, userId, name, role, description, avatarData, size = 'sm', showRole = false, compact = false, hideAvatar = false, className = '' }) {
  const { user, jwtToken } = useAuth();
  const { findPerson } = usePeople();
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [starting, setStarting] = useState(false);
  const [pingError, setPingError] = useState('');
  const fallback = useMemo(() => ({ id: userId, name, role, description, avatarData }), [userId, name, role, description, avatarData]);
  const found = findPerson(person?.id || person?._id || userId, person?.name || name);
  const resolved = { ...fallback, ...(person || {}), ...(found || {}) };
  const resolvedId = String(resolved.id || resolved._id || '');
  const isMe = resolvedId && resolvedId === String(user?.id || user?._id || '');
  const canPrivatePing = user?.role !== 'client' && resolved.role !== 'client';

  const toggle = (event) => {
    event?.stopPropagation();
    setPingError('');
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.min(310, window.innerWidth - 16);
      setPosition({ top: Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 260)), left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), width });
    }
    setOpen((value) => !value);
  };

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => { if (!triggerRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === 'Escape') setOpen(false); };
    const resize = () => setOpen(false);
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', resize);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', resize);
    };
  }, [open]);

  const ping = async () => {
    if (!resolvedId || isMe || starting) return;
    setStarting(true);
    try {
      const conversation = await apiStartPrivatePing(jwtToken, resolvedId);
      window.dispatchEvent(new CustomEvent('ping:refresh'));
      navigate(`/portal/pings?conversation=${conversation.id}`);
      setOpen(false);
    } catch (error) {
      setPingError(error.message || 'Could not open this Ping');
    } finally { setStarting(false); }
  };

  const popover = open && position ? ReactDOM.createPortal(
    <section ref={popoverRef} className="person-popover" style={position} role="dialog" aria-label={`${resolved.name || 'User'} profile`}>
      <button type="button" className="person-popover-close" onClick={() => setOpen(false)} aria-label="Close profile"><X size={15} /></button>
      <PersonAvatar person={resolved} size="xl" />
      <div className="person-popover-copy"><strong>{resolved.name || 'Portal user'}</strong><span>{roleLabel(resolved.role)}</span>{resolved.description && resolved.role !== 'client' && <p>{resolved.description}</p>}</div>
      {pingError && <span className="person-popover-error">{pingError}</span>}
      {isMe ? <span className="person-popover-self"><UserRound size={15} />This is your profile</span> : canPrivatePing ? <button type="button" className="btn btn-primary person-popover-ping" onClick={ping} disabled={!resolvedId || starting}><MessageCircle size={16} />{starting ? 'Opening…' : 'Ping privately'}</button> : null}
    </section>,
    document.body,
  ) : null;

  return (
    <>
      <button ref={triggerRef} type="button" className={`user-identity ${compact ? 'compact' : ''} ${className}`.trim()} onClick={toggle} aria-haspopup="dialog" aria-expanded={open}>
        {!hideAvatar && <PersonAvatar person={resolved} size={size} />}
        {!compact && <span className="user-identity-copy"><strong>{resolved.name || 'Portal user'}</strong>{showRole && <small>{roleLabel(resolved.role)}</small>}</span>}
      </button>
      {popover}
    </>
  );
}

export function PingIndicator() {
  const { jwtToken, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const load = useCallback(async () => {
    if (!jwtToken || user?.role === 'client') return;
    try { setConversations(await apiGetPingConversations(jwtToken)); } catch { /* retain last result */ }
  }, [jwtToken, user?.role]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, 15000);
    window.addEventListener('ping:refresh', load);
    return () => { clearInterval(timer); window.removeEventListener('ping:refresh', load); };
  }, [load]);

  useEffect(() => { if (location.pathname.includes('/pings')) load(); }, [location.pathname, load]);
  const unread = conversations.filter((conversation) => conversation.unreadCount > 0);
  const count = unread.reduce((total, conversation) => total + conversation.unreadCount, 0);
  const allSenders = unread.map((conversation) => conversation.lastMessage?.author || conversation.members.find((member) => String(member.id) !== String(user?.id))).filter(Boolean);
  const senders = allSenders.slice(0, 4);
  if (user?.role === 'client') return null;
  return (
    <button type="button" className={`ping-indicator ${count ? 'unread' : ''}`} onClick={() => navigate(unread[0] ? `/portal/pings?conversation=${unread[0].id}` : '/portal/pings')} aria-label={count ? `${count} unread Pings` : 'Open Pings'} title="Pings">
      {senders.length ? <span className="ping-indicator-avatars">{senders.map((person, index) => <PersonAvatar key={`${person.id}-${index}`} person={person} size="xs" unread />)}{allSenders.length > 4 && <i className="ping-indicator-more">+{allSenders.length - 4}</i>}</span> : <MessagesSquare size={18} />}
      {count > 0 && <span className="ping-indicator-count">{count > 99 ? '99+' : count}</span>}
    </button>
  );
}
